import { queryOptions } from '@tanstack/react-query'
import { apiGet, apiPost, type PostValue } from './client'
import type { TablePage, TableRequest } from './softsee'
import type { Period } from './securitee'

/** Saveenergee - /api/v2/saveenergee/*; perechile vechi sunt in `SaveenergeeController`. */

function params(r: Omit<TableRequest, 'list'>, extra: Record<string, string> = {}): URLSearchParams {
  const p = new URLSearchParams()
  p.set('draw', String(r.draw))
  p.set('start', String(r.start))
  p.set('length', String(r.length))
  p.set('order[0][column]', String(r.orderColumn))
  p.set('order[0][dir]', r.orderDir)
  p.set(`columns[${r.orderColumn}][name]`, r.orderName)
  p.set('search[value]', r.search)
  for (const [k, v] of Object.entries(extra)) p.set(k, v)
  return p
}

export type Row = Record<string, unknown>

/** Lista masinilor aduce si moneda firmei (`it_saveenergee_saving_settings.currency`). */
export type ListPage = TablePage<Row> & { currency?: string }

export function devicesTableQuery(r: Omit<TableRequest, 'list'> & Period) {
  return queryOptions({ queryKey: ['saveenergee', 'devices', r], queryFn: () => apiGet<ListPage>(`/saveenergee/devices-table?${params(r, { from: r.from, to: r.to })}`) })
}

export function eventsTableQuery(r: Omit<TableRequest, 'list'> & Period) {
  return queryOptions({ queryKey: ['saveenergee', 'events', r], queryFn: () => apiGet<ListPage>(`/saveenergee/events-table?${params(r, { from: r.from, to: r.to })}`) })
}

export interface Dashboard {
  currency: string
  last30DaysSaved: number
  lastYearSaved: number
  averageSavedDevice: number
  averageRunningTime: number
  activeDevices: number
}

export function dashboardQuery() {
  return queryOptions({ queryKey: ['saveenergee', 'dashboard'], queryFn: () => apiGet<Dashboard>('/saveenergee/dashboard') })
}

export interface Saved {
  timeSaved: { monitorSleep: number; autoHibernate: number; total: number }
  energySaved: { monitor: number; hibernate: number; total: number }
  moneySaved: { monitor: number; hibernate: number; total: number }
}

export interface DeviceSummary {
  currency: string
  watts: { device: number; monitor: number; price: number }
  totals: { hibernate_time: number; auto_hibernate: number; user_hibernate: number; active_time: number; monitor_sleep_time: number; shutdown_time: number } | null
  saved: Saved | null
  yearly: Saved | null
  daily: { day: string; active_time: number; auto_hibernate: number; user_hibernate: number; monitor_sleep_time: number; shutdown_time: number; energy: number; money: number }[]
}

export function summaryQuery(id: number, period: Period) {
  return queryOptions({ queryKey: ['saveenergee', 'devices', id, 'summary', period], queryFn: () => apiGet<DeviceSummary>(`/saveenergee/devices/${id}/summary?from=${period.from}&to=${period.to}`) })
}

export function nestedTableQuery(id: number, what: 'activity' | 'events', r: Omit<TableRequest, 'list'> & Partial<Period>) {
  return queryOptions({
    queryKey: ['saveenergee', 'devices', id, what, r],
    queryFn: () => apiGet<TablePage<Row>>(`/saveenergee/devices/${id}/${what}/table?${params(r, r.from && r.to ? { from: r.from, to: r.to } : {})}`),
  })
}

export interface Day { day: number; name: string; start: string; end: string; checked: boolean }

export interface DeviceSettings {
  own: boolean
  hibernate: string
  monitor_sleep: string
  profile: number
  days: Day[]
  profileDays: Day[] | null
  profileName: string | null
  profiles: { ID: number; profile_name: string }[]
}

export function deviceSettingsQuery(id: number) {
  return queryOptions({ queryKey: ['saveenergee', 'devices', id, 'settings'], queryFn: () => apiGet<DeviceSettings>(`/saveenergee/devices/${id}/settings`) })
}

export function post(path: string, fields: Record<string, PostValue> = {}) {
  return apiPost<{ ok: boolean }>(path, fields)
}
